import axios from 'axios';

const api = axios.create({
  baseURL: process.env.REACT_APP_API_URL || '/api',
  timeout: 30000,
  headers: { 'Content-Type': 'application/json' },
  withCredentials: true, // send httpOnly refresh cookie automatically
});

// Attach stored access token
const token = localStorage.getItem('token');
if (token) api.defaults.headers.common['Authorization'] = `Bearer ${token}`;

// ── JWT expiry warning ────────────────────────────────────────────────────────
// Fires a custom DOM event 5 minutes before the token expires so a toast
// can warn the user before they get silently logged out mid-session.
function scheduleExpiryWarning() {
  const tok = localStorage.getItem('token');
  if (!tok) return;
  try {
    const payload = JSON.parse(atob(tok.split('.')[1]));
    const expiresIn = payload.exp * 1000 - Date.now();
    const warnIn    = expiresIn - 5 * 60 * 1000;
    if (warnIn > 0) setTimeout(() => window.dispatchEvent(new Event('session-expiring')), warnIn);
  } catch { /* invalid token shape */ }
}
scheduleExpiryWarning();

// ── Token refresh ─────────────────────────────────────────────────────────────
// On 401, try to silently refresh using httpOnly cookie (web) or
// localStorage refresh_token (Capacitor). Queue concurrent requests while
// refresh is in flight; reject all if refresh itself fails.
let isRefreshing = false;
let failedQueue = [];

const processQueue = (error, newToken = null) => {
  failedQueue.forEach(p => error ? p.reject(error) : p.resolve(newToken));
  failedQueue = [];
};

function clearSession() {
  localStorage.removeItem('token');
  localStorage.removeItem('refresh_token');
  delete api.defaults.headers.common['Authorization'];
  window.dispatchEvent(new Event('session-expired'));
  window.location.href = '/login';
}

api.interceptors.response.use(
  res => res,
  async err => {
    const original = err.config;

    // Only attempt refresh on 401, not on refresh/login calls themselves
    if (err.response?.status !== 401 || original._retry || original.url?.includes('/auth/refresh')) {
      return Promise.reject(err);
    }

    if (isRefreshing) {
      // Queue this request until the in-flight refresh resolves
      return new Promise((resolve, reject) => {
        failedQueue.push({ resolve, reject });
      }).then(newToken => {
        original.headers['Authorization'] = `Bearer ${newToken}`;
        return api(original);
      });
    }

    original._retry = true;
    isRefreshing = true;

    try {
      // Cookie is sent automatically (withCredentials). Also send stored
      // refresh_token in body for Capacitor where cookies don't work.
      const refreshPayload = {};
      const stored = localStorage.getItem('refresh_token');
      if (stored) refreshPayload.refresh_token = stored;

      const res = await api.post('/auth/refresh', refreshPayload);
      const newToken = res.data.token;

      localStorage.setItem('token', newToken);
      api.defaults.headers.common['Authorization'] = `Bearer ${newToken}`;
      scheduleExpiryWarning();
      processQueue(null, newToken);
      original.headers['Authorization'] = `Bearer ${newToken}`;
      return api(original);
    } catch (refreshErr) {
      processQueue(refreshErr, null);
      clearSession();
      return Promise.reject(refreshErr);
    } finally {
      isRefreshing = false;
    }
  }
);

export const bulkUpload = (path, file, params = {}) => {
  const fd = new FormData();
  fd.append('file', file);
  return api.post(path, fd, {
    headers: { 'Content-Type': 'multipart/form-data' },
    params,
  });
};

export default api;
